import React, { useEffect, useRef } from 'react';
import { HubMarketingNav } from '../../components/ui/HubHeader';
import { Link } from 'react-router-dom';
import AccountingHeroSection from './HeroSection';
import AccountingPricingSection from './PricingSection';
import TrustBar from '../homepage/components/TrustBar';
import FeaturesSection from '../homepage/components/FeaturesSection';
import TestimonialsSection from '../homepage/components/TestimonialsSection';
import CTASection from '../homepage/components/CTASection';
import DiscoveryTextSection from '../homepage/components/DiscoveryTextSection';
import FAQSection from '../homepage/components/FAQSection';
import Seo from '../../components/Seo';
import { FEATURED_FAQS, getFaqPageSchemaForFaqs } from '../../content/faqs';
import { appendTrackingParams } from '../../utils/tracking';

/**
 * Classic long-form Neevii Accounting marketing landing (pre–product-hub home).
 * Linked from the hub first card and header instead of sending users straight to the web app.
 */
const AccountingMarketingPage = () => {
  const appUrl = (import.meta.env.VITE_APP_URL || 'https://app.neevii.com/').replace(/\/$/, '');
  const heroRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  const scrollToHero = () => {
    heroRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Seo
        title="Neevii — Accounting Built for Privacy. No Subscription. No Lock-In."
        description="Drop in your CSV, automate your bookkeeping locally, and keep your financial data strictly offline. Pay once, own forever."
        schema={getFaqPageSchemaForFaqs(FEATURED_FAQS)}
      />
      <div className="min-h-screen text-foreground">
        <HubMarketingNav preset="accounting" />
        <main className="pt-14 md:pt-16">
          <div ref={heroRef}>
            <AccountingHeroSection />
          </div>
          <TrustBar />
          <AccountingPricingSection onWaitlistClick={scrollToHero} />
          <DiscoveryTextSection />
          <FeaturesSection />
          <TestimonialsSection />
          <FAQSection />
          <CTASection />
        </main>
        <footer className="border-t border-border bg-card/30 py-12 backdrop-blur-sm">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mb-8 grid grid-cols-1 gap-8 sm:grid-cols-3">
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground">Company</h3>
                <div className="space-y-2">
                  <Link
                    to="/updates"
                    className="block text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Updates · New Features
                  </Link>
                  <a
                    href="mailto:support@neevii.com"
                    className="block text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Contact
                  </a>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground">Product</h3>
                <div className="space-y-2">
                  <Link
                    to="/subscription"
                    className="block text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Pricing
                  </Link>
                  <Link to="/faq" className="block text-sm text-muted-foreground transition-colors hover:text-foreground">
                    FAQ
                  </Link>
                  <Link
                    to="/facts-for-ai"
                    className="block text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Facts for AI
                  </Link>
                  <Link
                    to="/tutorials"
                    className="block text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Tutorials
                  </Link>
                  <Link
                    to="/sheets"
                    className="block text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Sheets Sidebar
                  </Link>
                  <a
                    href={appendTrackingParams(appUrl)}
                    className="block text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Start Free Trial
                  </a>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground">Legal</h3>
                <div className="space-y-2">
                  <Link
                    to="/privacy"
                    className="block text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Privacy Policy
                  </Link>
                  <Link
                    to="/terms"
                    className="block text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Terms of Service
                  </Link>
                  <Link
                    to="/security"
                    className="block text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Security
                  </Link>
                  <Link
                    to="/support-sheets"
                    className="block text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Sheets Sidebar Support
                  </Link>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center justify-between space-y-4 border-t border-border pt-8 lg:flex-row lg:space-y-0">
              <Link
                to="/"
                className="text-base font-semibold text-foreground transition-colors hover:text-primary"
                aria-label="Neevii product hub"
              >
                Neevii
              </Link>
              <div className="text-sm text-muted-foreground">© {new Date()?.getFullYear()} Neevii. All rights reserved.</div>
              <div className="flex items-center space-x-4">
                <a href="#" className="text-muted-foreground transition-colors hover:text-foreground" aria-label="Twitter (coming soon)">
                  <span className="sr-only">Twitter</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
                  </svg>
                </a>
                <a href="#" className="text-muted-foreground transition-colors hover:text-foreground" aria-label="LinkedIn (coming soon)">
                  <span className="sr-only">LinkedIn</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </a>
              </div>
            </div>
            <nav
              className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 border-t border-white/10 bg-black/40 px-4 py-5 -mx-6 sm:-mx-6 lg:-mx-8 sm:px-6 lg:px-8"
              aria-label="Quick links"
            >
              <Link className="text-sm text-white/70 transition-colors hover:text-[#ffbe33]" to="/facts-for-ai">
                Facts for AI
              </Link>
              <Link className="text-sm text-white/70 transition-colors hover:text-[#ffbe33]" to="/subscription">
                Pricing
              </Link>
              <Link className="text-sm text-white/70 transition-colors hover:text-[#ffbe33]" to="/faq">
                FAQ
              </Link>
              <Link className="text-sm text-white/70 transition-colors hover:text-[#ffbe33]" to="/privacy">
                Privacy
              </Link>
              <a
                className="text-sm text-white/70 transition-colors hover:text-[#ffbe33]"
                href={appendTrackingParams(appUrl)}
              >
                App
              </a>
            </nav>
          </div>
        </footer>
      </div>
    </>
  );
};

export default AccountingMarketingPage;
